import { t } from '@/locales'

/**
 * Appends the internal marker to a customer or order label — "Kunde A" → "Kunde A (intern)".
 *
 * A plain suffix rather than a chip, so the marker travels with the name everywhere it is
 * rendered: table cells, modal titles, dropdowns, search results. A chip only exists where
 * a component happens to support one, and `UiDynamicTable` in particular renders a cell's
 * chip *instead of* its text, which erased the customer name outright (MIL-252).
 *
 * Always fed from `resolveIsInternal()` on the account's or the order's own options —
 * never from a live lookup, so a historical order keeps the marker it was created with.
 */
export function withInternalSuffix(label: string, isInternal: boolean): string {
  // An empty label stays empty: a record read before its document has loaded would otherwise
  // render as a bare " (intern)" with nothing in front of it.
  if (!isInternal || !label) return label
  return `${label} ${t('term.internal-suffix')}`
}

/**
 * An order number formatted for display — "#1043", or "#1043 (intern)" for an internal
 * order. Always fed from the *order's* own `options.isInternal` snapshot, never from the
 * customer account's current flag, so an order created while the account was internal keeps
 * the marker after the account is switched back (MIL-252).
 *
 * Display only. The report templates' `orderNumber` variable deliberately does not go
 * through this — it is rendered into the customer's PDF (see OrderReportPreviewModal).
 */
export function formatOrderNumber(
  orderNumber: string,
  isInternal: boolean
): string {
  return withInternalSuffix(`#${orderNumber}`, isInternal)
}
